import { useTranslation } from 'react-i18next';

import type { ControlMessageCategory } from '@/types/category';
import type { PredefinedMessageFormValues } from '@/types/predefined-messages';
import type { RoleFilterOptions } from '@/types/roles';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { ADD } from '@/constants';
import { collapseRolesToAll, getRoleDisplayName } from '@/lib/helpers/role-mapper';
import { cn } from '@/lib/utils';
import { ALL_AVAILABLE_TO_ROLE_KEYS } from '@/utils/constants/roles';

const PRIORITY_RECIPIENT_ROLE = 'lf';

export const PREDEFINED_MESSAGE_FORM_FILTER_SURFACE_CLASS = cn(
  'border border-chat-send-stroke bg-chat-message-bg shadow-[var(--shadow-predefined-filter-md)]'
);

export function usePredefinedMessageFormHelpers() {
  const { t } = useTranslation();

  function getPredefinedMessageFormTitle({ mode }: { mode: string }): string {
    return t(
      mode === ADD
        ? 'configuration.predefinedMessages.form.addTitle'
        : 'configuration.predefinedMessages.form.editTitle'
    );
  }

  function getCategoryDisplayForPredefinedMessage({
    categories,
    predefinedMessageCategoryId,
  }: {
    categories: ControlMessageCategory[];
    predefinedMessageCategoryId: number | undefined;
  }): { label: string; isPlaceholder: boolean } {
    const cat = categories.find(({ id }) => id === predefinedMessageCategoryId);

    return {
      label: cat?.name ?? t('configuration.predefinedMessages.form.selectCategory'),
      isPlaceholder: !cat,
    };
  }

  function getAvailableToSummary({
    availableToIds,
    availableToTargets,
  }: {
    availableToIds: string[];
    availableToTargets: RoleFilterOptions[];
  }): string {
    if (!availableToIds?.length) {
      return t('configuration.predefinedMessages.form.selectRoles');
    }

    const targetSummaryById = new Map(
      availableToTargets.map(({ id, shortName }) => [id, shortName])
    );

    return collapseRolesToAll(availableToIds, ALL_AVAILABLE_TO_ROLE_KEYS)
      .flatMap((id) => {
        const shortName = targetSummaryById.get(id);

        return shortName ? [getRoleDisplayName(shortName, t)] : [];
      })
      .join(', ');
  }

  return {
    getPredefinedMessageFormTitle,
    getCategoryDisplayForPredefinedMessage,
    getAvailableToSummary,
  };
}

export function getPredefinedMessageFormDefaults({
  initialValues,
  defaultCategoryId,
}: {
  initialValues: PredefinedMessageFormValues | undefined;
  defaultCategoryId: number;
}): PredefinedMessageFormValues {
  if (initialValues) {
    return { ...initialValues, prioritized: initialValues.prioritized ?? false };
  }

  return {
    message: '',
    predefinedMessageCategoryId: defaultCategoryId,
    availableToIds: [],
    recipientIds: [],
    prioritized: false,
  };
}

export function areAllRecipientsSelected({
  allRecipientIds,
  recipientIds,
}: {
  allRecipientIds: string[];
  recipientIds: string[];
}): boolean {
  if (!allRecipientIds?.length) {
    return false;
  }

  const recipientSet = new Set(recipientIds);

  return allRecipientIds.every((id) => recipientSet.has(id));
}

export function toggleValueInList({ current, id }: { current: string[]; id: string }): string[] {
  if (current.includes(id)) {
    return current.filter((x) => x !== id);
  }

  return [...current, id];
}

export function getRecipientIdsAfterSelectAllToggle({
  allRecipientIds,
  allCurrentlySelected,
}: {
  allRecipientIds: string[];
  allCurrentlySelected: boolean;
}): string[] {
  return allCurrentlySelected ? [] : [...allRecipientIds];
}

export function toggleRecipientIdInPredefinedMessageForm({
  getValues,
  setValue,
  id,
}: {
  getValues: UseFormGetValues<PredefinedMessageFormValues>;
  setValue: UseFormSetValue<PredefinedMessageFormValues>;
  id: string;
}): void {
  setValue('recipientIds', toggleValueInList({ current: getValues('recipientIds'), id }), {
    shouldDirty: true,
    shouldValidate: true,
  });
}

export function toggleAllRecipientsInPredefinedMessageForm({
  setValue,
  allRecipientIds,
  allCurrentlySelected,
}: {
  setValue: UseFormSetValue<PredefinedMessageFormValues>;
  allRecipientIds: string[];
  allCurrentlySelected: boolean;
}): void {
  setValue(
    'recipientIds',
    getRecipientIdsAfterSelectAllToggle({ allRecipientIds, allCurrentlySelected }),
    { shouldDirty: true, shouldValidate: true }
  );
}

export function isPriorityRecipientSelected(recipientIds: string[]): boolean {
  return recipientIds.includes(PRIORITY_RECIPIENT_ROLE);
}

export function resetPrioritizedIfPriorityRecipientRemoved({
  getValues,
  setValue,
}: {
  getValues: UseFormGetValues<PredefinedMessageFormValues>;
  setValue: UseFormSetValue<PredefinedMessageFormValues>;
}): void {
  if (getValues('prioritized') && !isPriorityRecipientSelected(getValues('recipientIds'))) {
    setValue('prioritized', false, { shouldDirty: true });
  }
}

export function getAvailableToIdsAfterToggleAll({
  checked,
  allTargetIds,
}: {
  checked: boolean;
  allTargetIds: string[];
}): string[] {
  return checked ? [...allTargetIds] : [];
}

export function getAllAvailableToTargetIds({
  targets,
}: {
  targets: RoleFilterOptions[];
}): string[] {
  return targets.map(({ id }) => id);
}
